define((require) => {
    "use strict";

    const {contains, extend}              = require("underscore");
    const AnalyticsController             = require("ui/settings/analytics/AnalyticsController");
    const AuthenticationController        = require("security/AuthenticationController");
    const Backbone                        = require("backbone");
    const BreakpointController            = require("ui/settings/breakpoint/BreakpointController");
    const ConfirmationManager             = require("services/ConfirmationManager");
    const DashboardConfiguration          = require("ui/dashboard/DashboardConfiguration");
    const DashboardController             = require("ui/dashboard/DashboardController");
    const EditorController                = require("ui/editor/EditorController");
    const EditorPreviewController         = require("ui/editor/EditorPreviewController");
    const ExperienceURLProvider           = require("ui/experience/ExperienceURLProvider");
    const ExportController                = require("ui/settings/export/ExportController");
    const FilteringRouter                 = require("zbone/FilteringRouter");
    const IntegrationsController          = require("editor/ui/settings/integrations/IntegrationsController");
    const LoggedIn                        = require("shared/security/LoggedIn");
    const NavBarView                      = require("ui/common/NavBarView");
    const NotificationBar                 = require("editor/ui/notificationbar/NotificationBar");
    const ProjectDashboardConfiguration   = require("ui/dashboard/ProjectDashboardConfiguration");
    const ProjectDashboardController      = require("ui/projects/ProjectDashboardController");
    const ManageAccountController         = require("ui/settings/manageAccount/ManageAccountController");
    const PSDImportController             = require("ui/settings/psdImport/PSDImportController");
    const ManageFontsController           = require("editor/ui/settings/fonts/ManageFontsController");
    const LoggedInUserState               = require("shared/ui/user/LoggedInUserState");
    const {Logger}                        = require("shared/util/Logger");
    const PublishController               = require("ui/publish/PublishController");
    const Share                           = require("ui/share/Share");
    const StringUtils                     = require("shared/util/StringUtils");
    const Window                          = require("shared/services/Window");
    const ZendeskErrorView                = require("ui/zendesk/ZendeskErrorView");
    const ZendeskUtil                     = require("util/ZendeskUtil");
    const Configuration                   = require("shared/services/Configuration");
    const Storage                         = require("shared/services/Storage");

    const AUTH0_DOMAIN = Configuration.auth0Domain;
    const AUTH0_CLIENT_ID = Configuration.auth0ClientId;
    const CONTENT_CONTROL_URL = Configuration.contentControlUrl;

    const logger = new Logger("EditorRouter");

    /**
     * Get a (default) prompt message to alert to user on before navigate, if any.
     *
     * @param {Boolean|String} prompt    The message to prompt the user for if a string, or a true value for the
     *                                   string "You are leaving a page with unsaved changes.". An empty string or
     *                                   a falsey value will return undefined.
     * @param {String}         [postfix] Optional postfix string to append to (the default) <tt>prompt</tt>, if not
     *                                   already.
     * @return {String|undefined} The message to prompt to the user; or undefined. Note that if the prompt is used as part of
     *                  <tt>Window.unload(..)</tt>, it will be alerted in a special user prompt managed by the
     *                  browser (nothing to do about this).
     */
    function getPrompt(prompt, postfix) {
        if (prompt) {
            if (typeof prompt !== "string") {
                prompt = "You are leaving a page with unsaved changes.";
            }

            if (postfix && prompt.indexOf(postfix) !== 0) {
                prompt += `\n\n${postfix}`;
            }

            return prompt;
        }

        return undefined;
    }

    /**
     * Top-level router for the Creator Editor app.
     *
     * @author Bo Gotthardt
     * @class EditorRouter
     */
    return FilteringRouter.extend({
        /**
         * Keeps track of whether we are showing the default nav bar view to avoid reconstructing it.
         */
        _showingDefaultNavBarView: false,

        _navBarView: null,

        /**
         * Routes that skip the login check.
         */
        _skipLoginRoutes: [
            "adLogin*params",
            "communityLogin",
            "user/password/forgot",
            "user/password/:resetToken",
            "browsersupport"
        ],

        /**
         * @private
         *
         * Shows the default nav bar view in the nav bar. If the nav bar is already showing the default nav bar view,
         * has no effect.
         *
         * @param {Object} [options] Optional. The default navigation bar options.
         *
         * @cfg {Boolean} [withoutPrincipal] True to show the default nav bar view without the principal
         *                                   information, false to include it (default).
         * @cfg {ProjectModel} [project]     The current project, if any.
         */
        _showDefaultNavBarView(options) {
            // Clear cache...
            this._navBarRegion.close();

            if (!this._showingDefaultNavBarView) {
                options = options || {};

                const principal = options.withoutPrincipal ? {} : LoggedIn.principal();

                this._navBarView = new NavBarView({
                    principal,
                    project: options.project,
                    experience: options.experience,
                    viewModel: options.viewModel
                });

                this._navBarRegion.show(this._navBarView);
            }
        },

        initialize(options) {
            this.options = options;
        },

        start() {
            this._mainRegion   = this.options.mainRegion;
            this._navBarRegion = this.options.navBarRegion;

            this._navBarView = new NavBarView({
                principal: LoggedIn.principal()
            });

            this._editorPreviewController = new EditorPreviewController({
                region: this._mainRegion,
                navBarRegion: this._navBarRegion
            });

            this._projectController = new ProjectDashboardController({
                region: this._mainRegion
            });

            // Not nice, but currently the publish toolbar view is just a view like any other view, though it is
            // in effect its own application, running by itself, needing special loading of pushed experiences
            // and what not!
            const hash = window.location.hash;
            const publishToolbar = hash.indexOf("publishtoolbar") !== -1;

            let loaderOptions;

            if (publishToolbar) {
                const hasProjectContext = !!hash.match(/project\/\w+\/publishtoolbar/);

                loaderOptions = {
                    experiences: {
                        includeMeasureData: false,
                        pushedOnly: true
                    },
                    groups: {
                        pushedOnly: true
                    },
                    loadProjects: hasProjectContext
                };
            } else {
                loaderOptions = {
                    experiences: {
                        includeMeasureData: true
                    }
                };
            }

            this._authController = new AuthenticationController({
                region:       this._mainRegion,
                navBarRegion: this._navBarRegion,
                loader:       loaderOptions
            });

            this._dashboardController = new DashboardController({
                region: this._mainRegion
            });

            this._manageAccountController = new ManageAccountController({
                region: this._mainRegion
            });

            this._psdImportController = new PSDImportController({
                region: this._mainRegion
            });

            this._exportController = new ExportController({
                region: this._mainRegion
            });

            this._analyticsController = new AnalyticsController({
                region: this._mainRegion
            });

            this._manageFontsController = new ManageFontsController({
                region: this._mainRegion
            });

            this._integrationsController = new IntegrationsController({
                region: this._mainRegion
            });

            this._breakpointController = new BreakpointController({
                region: this._mainRegion
            });

            this._publishController = new PublishController({
                region: this._mainRegion
            });

            // Keep track of whether the nav bar is showing the default view
            this.listenTo(this._navBarRegion, "show", function(view) {
                this._showingDefaultNavBarView = view instanceof NavBarView;
            });

            this.listenTo(this._navBarRegion, "close", function() {
                this._showingDefaultNavBarView = false;
            });

            // Hook up our beforeNavigate view logic to window unload. We cannot control the entire message, nor
            // button text being, shown here...
            Window.unload((e) => {
                const currentView = this._mainRegion.currentView;

                if (currentView && currentView.beforeNavigate) {
                    const prompt = getPrompt(currentView.beforeNavigate());

                    if (prompt) {
                        e.originalEvent.returnValue = prompt;
                        return prompt;
                    }
                }
                // Undefined
            });
        },

        routes: {
            "": function() {
                this._showDefaultNavBarView();

                LoggedInUserState.set("location", LoggedInUserState.Location.DASHBOARD_EXPERIENCES);
                this._dashboardController.show({tab: DashboardConfiguration.EXPERIENCES});

                return this._dashboardController;
            },

            "images/details/:id/experiences": function(id) {
                this._showDefaultNavBarView();

                LoggedInUserState.set("location", LoggedInUserState.Location.DASHBOARD_IMAGES);
                this._dashboardController.show({
                    tab: DashboardConfiguration.IMAGES,
                    partialViewOptions: {id, showExperiences: true}
                });

                return this._dashboardController;
            },

            "images/details/:id": function(id) {
                this._showDefaultNavBarView();

                LoggedInUserState.set("location", LoggedInUserState.Location.DASHBOARD_IMAGES);
                this._dashboardController.show({
                    tab: DashboardConfiguration.IMAGES,
                    partialViewOptions: {id}
                });

                return this._dashboardController;
            },

            "images": function() {
                this._showDefaultNavBarView();

                LoggedInUserState.set("location", LoggedInUserState.Location.DASHBOARD_IMAGES);
                this._dashboardController.show({tab: DashboardConfiguration.IMAGES});

                return this._dashboardController;
            },

            "templates": function() {
                this._showDefaultNavBarView();

                LoggedInUserState.set("location", LoggedInUserState.Location.DASHBOARD_TEMPLATES);
                this._dashboardController.show({tab: DashboardConfiguration.TEMPLATES});

                return this._dashboardController;
            },

            "publish": function() {
                this._showDefaultNavBarView();

                LoggedInUserState.set("location", LoggedInUserState.Location.DASHBOARD_PUBLISH);
                this._dashboardController.show({tab: DashboardConfiguration.PUBLISH});

                return this._dashboardController;
            },
            "reporting": function() {
                this._showDefaultNavBarView();

                LoggedInUserState.set("location", LoggedInUserState.Location.REPORTING);
                this._dashboardController.show({
                    tab: DashboardConfiguration.REPORTING,
                    partialViewOptions: {matomoPage: LoggedInUserState.Location.REPORTING}
                });

                return this._dashboardController;
            },
            "reporting/segment/:segmentDimension": function(segmentDimension) {
                this._showDefaultNavBarView();

                LoggedInUserState.set("location", LoggedInUserState.Location.REPORTING_SEGMENT);
                this._dashboardController.show({
                    tab: DashboardConfiguration.REPORTING,
                    partialViewOptions: {matomoPage:  LoggedInUserState.Location.REPORTING_SEGMENT, segmentDimension: segmentDimension}
                });

                return this._dashboardController;
            },
            "user/password/forgot": function() {
                this._authController.showForgotPasswordForm();
            },

            "user/password/:resetToken": function(resetToken) {
                this._authController.loginAfterPasswordReset(resetToken);
            },

            "user/confirm/:confirmToken": function(confirmToken) {
                ConfirmationManager.confirm(confirmToken);
                // Navigate to main page, confirmation manager will handle the action...
                FilteringRouter.navigate("", true);
            },

            "user/requestConfirmEmail": function() {
                ConfirmationManager.requestConfirmationInfo(true);
                FilteringRouter.navigate("", true);
            },

            "user/zmags/:token": function(token) {
                this._authController.loginFromZmagsRequest(token);
            },

            "newuser/:token/share/:share_id": function(token, shareId) {
                Share.findById(shareId)
                    .then((share) => {
                        // Check if this is in fact the invited user that is currently logged in
                        const userIds = share.get("to").map((rawUser) => rawUser.id);

                        if (contains(userIds, LoggedIn.principal().get("userId"))) {
                            FilteringRouter.navigate(share.get("link"), {trigger: true, replace: true});
                        } else {
                            // Logged in with a different user (highly unlikely that we get to this point)
                            // Log out and re-navigate to the same route to trigger the "newuser" logic in the "before" hook
                            FilteringRouter.navigate("logout", {trigger: true, replace: true});
                            FilteringRouter.navigate(`newuser/${token}/share/${shareId}`, {trigger: true, replace: true});
                        }
                    })
                    .fail(() => {
                        FilteringRouter.navigate("", true);
                    });
            },

            "newuser/:token": function(token) {
                FilteringRouter.navigate(`newuser/${token}`, {trigger: true, replace: true});
            },

            "browsersupport": function() {
                this._authController.showBrowserSupport();
            },

            "experience/create": "_createExperience",
            "experience/create/project/:id": "_createExperience",

            // Project Routes
            "project/create": function() {
                this._projectController.createProject();
                LoggedInUserState.set("location", LoggedInUserState.Location.PROJECT_CREATE);

                return this._projectController;
            },

            "project/:id":      "_projectDashboard",

            "project/:id/images/details/:imageId/experiences": function(id, imageId) {
                return this._projectDashboard(id, "images", {
                    partialViewOptions: {id: imageId, showExperiences: true}
                });
            },

            "project/:id/images/details/:imageId": function(id, imageId) {
                return this._projectDashboard(id, "images", {
                    partialViewOptions: {id: imageId}
                });
            },

            "project/:id/:tab": "_projectDashboard",

            // Experience Routes
            "experience/:id/edit": function(id) {
                const experienceURL = ExperienceURLProvider.provide(id, ExperienceURLProvider.ACTION.EDIT);
                // FIXME: Use 'navigateLater' so that the controller returned by the "experience/:id/edit/:scene" route is not closed immediately...
                FilteringRouter.navigateLater(experienceURL, {trigger: true, replace: true});
            },

            "experience/:id/edit/:scene": function(id, scene) {
                this._openEditorAtSceneAndLayer(id, scene);
            },

            "experience/:id/edit/:scene/:layer": function(id, scene, layerName) {
                const redirectURL = ExperienceURLProvider.provide(id, ExperienceURLProvider.ACTION.EDIT, scene);
                Backbone.history.navigate(redirectURL, false);
                this._openEditorAtSceneAndLayer(id, scene, layerName);
            },

            "experience/:id/preview": function(id) {
                this._editorPreviewController.previewExperience(id);
                LoggedInUserState.set("location", LoggedInUserState.Location.PREVIEW);
            },

            "experience/:id/preview/:scene": function(id, scene) {
                this._openPreviewAtScene(id, scene);
            },

            "experience/:id/preview/:scene/:layer": function(id, scene, layerName) {
                const redirectURL = ExperienceURLProvider.provide(id, ExperienceURLProvider.ACTION.PREVIEW, scene);
                Backbone.history.navigate(redirectURL, false);
                this._openPreviewAtScene(id, scene, layerName);
            },

            "template/:id/preview": function(id) {
                this._openTemplatePreview(id);
            },

            "template/:id/preview/:scene": function(id, scene) {
                this._openTemplatePreview(id, scene);
            },

            "template/:id/preview/:scene/:layer": function(id, scene, layerName) {
                this._openTemplatePreview(id, scene, layerName);
            },

            "manageAccount": function() {
                this._showDefaultNavBarView();
                this._manageAccountController.showManageAccount();
                LoggedInUserState.set("location", LoggedInUserState.Location.MANAGE_ACCOUNT);
            },

            "psdImport": function() {
                window.location.href = "#designtransform";
            },

            "designtransform": function() {
                this._showDefaultNavBarView();
                this._psdImportController.showPSDImport();
                LoggedInUserState.set("location", LoggedInUserState.Location.PSD_IMPORT);
            },

            "export": function() {
                this._showDefaultNavBarView();
                this._exportController.showExport();
                LoggedInUserState.set("location", LoggedInUserState.Location.EXPORT);

                return this._exportController;
            },

            "analytics": function() {
                this._showDefaultNavBarView();
                this._analyticsController.showAnalytics();
                LoggedInUserState.set("location", LoggedInUserState.Location.ANALYTICS);

                return this._analyticsController;
            },

            "integrations": function() {
                this._showDefaultNavBarView();
                this._integrationsController.showIntegrations();
                LoggedInUserState.set("location", LoggedInUserState.Location.INTEGRATIONS);

                return this._integrationsController;
            },

            "breakpoints": function() {
                this._showDefaultNavBarView();
                this._breakpointController.showBreakpointSettingsView();
                LoggedInUserState.set("location", LoggedInUserState.Location.BREAKPOINT);

                return this._breakpointController;
            },

            "integrations-salesforce-commerce": function() {
                this._showDefaultNavBarView();
                this._integrationsController.showIntegrations("add-ons");
                LoggedInUserState.set("location", LoggedInUserState.Location.INTEGRATIONS);

                return this._integrationsController;
            },

            "fonts": function() {
                // Temp sanity check
                const integrations = LoggedInUserState.get("integrations");

                if (!integrations.fontIntegrationsEnabled()) {
                    return FilteringRouter.navigate("");
                }

                this._showDefaultNavBarView();

                this._manageFontsController.showManageFonts(integrations.fontIntegrations());

                LoggedInUserState.set("location", LoggedInUserState.Location.FONTS);

                return this._manageFontsController;
            },

            // Note: The followings are loaded in a different url, defacto as a standalone app!
            "project/:id/publishtoolbar/:url": function(id, url) {
                this._publishController.showToolbarView(url, id);
            },

            "project/:id/publishtoolbar/framed/:url": function(id, url) {
                this._publishController.showFramedToolbarView(url, id);
            },

            "publishtoolbar/:url": function(url) {
                this._publishController.showToolbarView(url);
            },

            "publishtoolbar/framed/:url": function(url) {
                this._publishController.showFramedToolbarView(url);
            },
            // End of Note

            "logout": function() {
                let signInURL = window.location.origin;
                const newDashboardExp = Storage.Local.get(Storage.Keys.NEW_DASHBOARD_EXP, Storage.Keys.NEW_DASHBOARD_EXP);
                NotificationBar.close({clear: "all"}); // remove *all* notifications on logout
                this._authController.logout();
                if (newDashboardExp) {
                    window.location.href = `${CONTENT_CONTROL_URL}#logout`;
                    return;
                }
                // Redirect to Auth0 for logout in case the user signed in with SSO
                // This will redirect back to the origin once the Auth0 session is cleared
                // If there is no session in Auth0, it will just redirect back immediately
                window.location.href = `${AUTH0_DOMAIN}/v2/logout?client_id=${AUTH0_CLIENT_ID}&returnTo=${signInURL}`;
            },

            "login": function() {
                this._showDefaultNavBarView();
                LoggedInUserState.set("location", LoggedInUserState.Location.LOGIN);
                this._authController.loginWithLoginForm(false, this.previousRoute || "" /* dashboard */);
            },

            "adLogin*params": function() {
                this._authController.logout();
                this._authController.loginWithLoginForm(true);
            },

            "communityLogin": function() {
                ZendeskUtil.login(this._authController);
            },

            "zendesk/error": function() {
                logger.error(`Zendesk Error: User with id: ${LoggedIn.principal().get("userId")} cannot access community portal, please contact Support`);
                this._showDefaultNavBarView({withoutPrincipal: true}); // true: no principal info
                const zendeskErrorView = new ZendeskErrorView();
                this._mainRegion.show(zendeskErrorView);
            },

            "*path": function(path) {
                logger.warn(`Unmatched route '${path}', redirecting to root.`);
                FilteringRouter.navigate("", true);
            }
        },

        /**
         * Handles the "create experience" routes.
         *
         * @param  {String} projectId Optional, if the experience is to be created inside a specific project.
         * @return {EditorController} The <tt>EditorController</tt> instance.
         */
        _createExperience(projectId) {
            EditorController.editNewExperience(projectId);

            return this._editorController;
        },

        /**
         * Handles the project dashboard routes.
         *
         * @param  {String} id   The project model id.
         * @param  {String} slug The URL slug part, if any.
         * @param {object} [options] The options passed to the projects controller.
         * @return {ProjectDashboardController} The <tt>ProjectDashboardController</tt> instance.
         */
        _projectDashboard(id, slug, options = {}) {
            const projectModel = LoggedInUserState.get("projects").get(id);

            const tab = ProjectDashboardConfiguration.fromSlug(slug);

            this._showDefaultNavBarView({project: projectModel});

            const showOptions = extend({project: projectModel, tab}, options);

            this._projectController.show(showOptions);

            switch (tab) {
                case ProjectDashboardConfiguration.IMAGES:
                    LoggedInUserState.set("location", LoggedInUserState.Location.PROJECT_DASHBOARD_IMAGES);
                    break;

                case ProjectDashboardConfiguration.PUBLISH:
                    LoggedInUserState.set("location", LoggedInUserState.Location.PROJECT_DASHBOARD_PUBLISH);
                    break;

                default:
                    LoggedInUserState.set("location", LoggedInUserState.Location.PROJECT_DASHBOARD_EXPERIENCES);
            }

            return this._projectController;
        },

        _skipLoginRoute(route) {
            return contains(this._skipLoginRoutes, route);
        },

        before(route, ...args) {
            // Most routes require the principal to be logged in, so show the login form instead if they are not
            // and block the route. Once they log in the route will be reloaded and this check will let them through
            // to the route.
            if (!LoggedIn.principal() && !this._skipLoginRoute(route)) { // TODO: this used to check for entire state
                // Handle new users separately, i.e. based on token...
                if (StringUtils.startsWith(route, "newuser/:token")) {
                    return this._authController.loginWithNewUserTokenOrLoginForm(args[0], StringUtils.contains(route, "share"));
                }

                if (StringUtils.startsWith(route, "experience/:id/edit/:scene")) {
                    const query = new URLSearchParams(window.location.search);
                    const ztoken = query.get('ztoken');
                    if(ztoken){
                        query.delete('ztoken');
                        const urlWithoutZtoken = `?${query.toString()}${window.location.hash}`;
                        return this._authController.loginWithToken(ztoken, urlWithoutZtoken, CONTENT_CONTROL_URL);
                    }
                }
                return this._authController.loginWithPersistedUserOrLoginForm();
            }

            const currentView = this._mainRegion.currentView;

            // Check if the current view wants to prompt the user before navigating away from it
            // The result is cached, since routing might otherwise prompt the user multiple times before the view is closed
            if (currentView && currentView.beforeNavigate) {
                const promptMessage = getPrompt(currentView.beforeNavigate(), "Are you sure you want to leave this page?");

                if (promptMessage) {
                    if (confirm(promptMessage)) {
                        // TODO: Refactor this, Why do we call restoreChannelList here, this goes against the least knowledge principle?
                        if (currentView.restoreChannelList) {
                            currentView.restoreChannelList();
                        }
                    } else if (this.lastSuccessfulRoute) {
                        FilteringRouter.navigate(this.lastSuccessfulRoute);

                        return false;
                    }
                }
            }

            return true;
        },

        /**
         * @param {String} experienceId
         * @param {number} sceneNumber
         * @param {String} [layerName] `name` property of the layer. See LayerEnum.
         */
        _openEditorAtSceneAndLayer(experienceId, sceneNumber, layerName) {
            if (LoggedInUserState.get("ready")) {
                this._setEditorDataToUser(experienceId, sceneNumber, layerName);
            } else {
                this.listenTo(LoggedInUserState, "change:ready", this._setEditorDataToUser.bind(this, experienceId, sceneNumber, layerName));
            }
        },

        _setEditorDataToUser(experienceId, sceneNumber, layerName) {
            const editorController = new EditorController({
                region: this._mainRegion,
                navBarRegion: this._navBarRegion
            });

            editorController.editExperience(experienceId, sceneNumber, layerName).done((options) => {
                LoggedInUserState.set("currentExperience", options.experience);
                LoggedInUserState.set("location", LoggedInUserState.Location.EDIT);
            });
        },

        /**
         * @param {String} [id] The experience ID
         * @param {number|string} [scene] The scene number
         * @param {String} [layerName] `name` property of the layer. See LayerEnum.
         */
        _openPreviewAtScene(id, scene, layerName) {
            this._editorPreviewController.previewExperience(id, parseInt(scene, 10) || undefined, layerName);
            LoggedInUserState.set("location", LoggedInUserState.Location.PREVIEW);
        },

        /**
         * @param {String} [id] The template ID
         * @param {number|string} [scene] - The scene number
         * @param {String} [layerName] `name` property of the layer. See LayerEnum.
         */
        _openTemplatePreview(id, scene, layerName) {
            this._editorPreviewController.previewTemplate(id, parseInt(scene, 10) || undefined, layerName);
            LoggedInUserState.set("location", LoggedInUserState.Location.PREVIEW_TEMPLATE);
        }

    }, {
        injection: {
            parameters: ["mainRegion", "navBarRegion"],
            asOptions: true
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/services/router/EditorRouter.js